/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.5 — BIRTH TIME RECTIFICATION (BTR)
 * CATEGORICAL EVIDENCE & VARIABLE CLUSTERING ENGINE (PHASE 4)
 * ============================================================================
 * Specification: VJ-BTR-V65-SPEC-001 v3.0, Section 8
 * Document ID: VJ-BTR-V65-SPEC-001
 * Architecture: GOV-062 [PROPOSED] Categorical Evidence Model, Variable
 * Clustering Taxonomy, and Contradiction Penalization.
 *
 * Core Principles:
 * 1. Zero Heuristic Floating-Point Scoring: Replaces arbitrary multipliers
 *    and threshold sums with transparent categorical evaluations:
 *    'SUPPORTS' | 'NEUTRAL' | 'CONTRADICTS' | 'INSUFFICIENT_DATA'.
 * 2. Four Physical Variable Clusters: Groups astrological techniques into
 *    four physical variable clusters to eliminate false independence claims:
 *    - CLUSTER_I_ASCENDANT: Diurnal Earth rotation (~24h cycle; D1, D9, D10 Lagnas, KP sub-lords)
 *    - CLUSTER_II_LUNAR: Geocentric Moon motion (~27.3d cycle; Vimshottari MD/AD)
 *    - CLUSTER_III_PLANETARY: Heliodynamic planetary longitudes (Jaimini Chara Karakas)
 *    - CLUSTER_IV_TRANSIT: Instantaneous planetary transits at event epoch (Guru-Shani Double Transit)
 * 3. 13 User-Selectable Canonical Life Event Categories:
 *    MARRIAGE, CHILDREN, CAREER, BUSINESS, RELOCATION, PROPERTY, HEALTH,
 *    FAMILY, EDUCATION, FINANCE, LEGAL, SPIRITUAL, OTHER.
 * 4. Critical Contradiction Disqualification:
 *    A single 'CRITICAL' contradiction from a 'TIER_A' event disqualifies
 *    a candidate plateau regardless of other supporting evidence.
 * 5. Common Jyotish Core: Uses authoritative calculation engines from
 *    `src/utils/vedicEngine.ts` and `src/utils/kpEngine.ts`.
 * ============================================================================
 */

import {
  AstrologicalPlateauInterval,
  BtrLifeEventInput,
  CategoricalEvidenceItem,
  CategoricalEvidenceStatus,
  ClusterEvidenceSummary,
  LifeEventCategory,
  LifeEventTier,
  PlateauEvidenceEvaluation,
  VariableClusterId
} from '../types/btr';
import {
  BirthDetails,
  CalculationContext,
  CompleteKundali,
  NodeConvention,
  PlanetName,
  PlanetPosition,
  ZodiacSign
} from '../types/kundali';
import {
  calculateKundali,
  calculateCharaKarakas,
  calculateDoubleTransit,
  calculateLivePlanetaryEphemeris,
  ZODIAC_SIGNS,
  ZODIAC_LORDS
} from './vedicEngine';
import { getKPLordsForLongitude } from './kpEngine';
import { isValidCalendarDate } from './btrContractValidation';

// ============================================================================
// 1. SHASHTRIC RULE DEFINITIONS & TREATISE CITATIONS
// ============================================================================

export interface ShastricCategoryConfig {
  category: LifeEventCategory;
  primaryHouses: number[];
  negationHouses: number[];
  dusthanaHouses: number[];
  primaryKarakas: PlanetName[];
  relevantVarga: 'D1' | 'D4' | 'D7' | 'D9' | 'D10' | 'D12' | 'D16' | 'D20' | 'D24' | 'D30';
  jaiminiKarakaRole: 'DK' | 'PK' | 'AmK' | 'MK' | 'AK' | 'BK' | 'GK';
  citations: {
    clusterI: string;
    clusterII: string;
    clusterIII: string;
    clusterIV: string;
  };
}

export const CANONICAL_SHASHTRIC_CONFIGS: Record<LifeEventCategory, ShastricCategoryConfig> = {
  MARRIAGE: {
    category: 'MARRIAGE',
    primaryHouses: [7, 2, 11],
    negationHouses: [6], // 12th from 7th = negation of marriage / legal separation
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Venus', 'Jupiter'],
    relevantVarga: 'D9',
    jaiminiKarakaRole: 'DK',
    citations: {
      clusterI: 'Brihat Parashara Hora Shastra (BPHS) Ch. 18 (Kalatra Bhava); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46 (Dasha Phalaadhyaya); Phaladeepika Ch. 14',
      clusterIII: 'Jaimini Upadesha Sutras 1.2.23 & 2.4.15 (Darakaraka Phala)',
      clusterIV: 'Devakeralam (Chandrakala Nadi) Vol. 1, Verse 1245 (Guru-Shani Gochar)'
    }
  },
  CHILDREN: {
    category: 'CHILDREN',
    primaryHouses: [5, 2, 9, 11],
    negationHouses: [4], // 12th from 5th = non-manifestation of progeny
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Jupiter'],
    relevantVarga: 'D7',
    jaiminiKarakaRole: 'PK',
    citations: {
      clusterI: 'BPHS Ch. 16 (Putra Bhava); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 12',
      clusterIII: 'Jaimini Upadesha Sutras 2.1.34 (Putrakaraka Sambandha)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Phaladeepika Ch. 26'
    }
  },
  CAREER: {
    category: 'CAREER',
    primaryHouses: [10, 6, 2, 11],
    negationHouses: [5, 9], // 12th from 6th (service cessation) and 12th from 10th (resignation)
    dusthanaHouses: [8, 12],
    primaryKarakas: ['Sun', 'Saturn', 'Mercury'],
    relevantVarga: 'D10',
    jaiminiKarakaRole: 'AmK',
    citations: {
      clusterI: 'BPHS Ch. 20 (Karma Bhava); Saravali Ch. 30; KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 16',
      clusterIII: 'Jaimini Upadesha Sutras 1.2.14 (Amatyakaraka Raja Yoga)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Phaladeepika Ch. 26'
    }
  },
  BUSINESS: {
    category: 'BUSINESS',
    primaryHouses: [7, 10, 3, 11],
    negationHouses: [6], // litigation / disruption of commercial partnerships
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Mercury', 'Jupiter'],
    relevantVarga: 'D10',
    jaiminiKarakaRole: 'AmK',
    citations: {
      clusterI: 'BPHS Ch. 18; Saravali Ch. 31; KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Prashna Marga Ch. 14',
      clusterIII: 'Jaimini Upadesha Sutras 1.2.14; BPHS Ch. 32',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Phaladeepika Ch. 26'
    }
  },
  RELOCATION: {
    category: 'RELOCATION',
    primaryHouses: [12, 9, 3],
    negationHouses: [4], // Fixed residence at birthplace
    dusthanaHouses: [6, 8],
    primaryKarakas: ['Moon', 'Rahu', 'Saturn'],
    relevantVarga: 'D4',
    jaiminiKarakaRole: 'MK',
    citations: {
      clusterI: 'BPHS Ch. 24 (Vyaya Bhava); Phaladeepika Ch. 14; KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46 (Deshantara Gamana Phala)',
      clusterIII: 'Jaimini Upadesha Sutras 2.1; BPHS Ch. 32',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Gochar Phala'
    }
  },
  PROPERTY: {
    category: 'PROPERTY',
    primaryHouses: [4, 2, 11],
    negationHouses: [3], // 12th from 4th = sale/dispossession of property
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Mars', 'Venus'],
    relevantVarga: 'D4',
    jaiminiKarakaRole: 'MK',
    citations: {
      clusterI: 'BPHS Ch. 15 (Bandhu Bhava & Vahana); Saravali Ch. 25; KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 16',
      clusterIII: 'Jaimini Upadesha Sutras 1.2; BPHS Ch. 32 (Matrikaraka)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Phaladeepika Ch. 26'
    }
  },
  HEALTH: {
    category: 'HEALTH',
    primaryHouses: [6, 8, 1, 12], // Roga, Randhra, Tanu, Vyaya
    negationHouses: [11], // 6th negation / recovery
    dusthanaHouses: [],   // In health events, dusthana activation supports manifestation
    primaryKarakas: ['Sun', 'Mars', 'Saturn', 'Rahu'],
    relevantVarga: 'D30',
    jaiminiKarakaRole: 'GK',
    citations: {
      clusterI: 'BPHS Ch. 17 (Roga Bhava); Charaka Samhita Astrological Core; KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46 (Arishtha Phalaadhyaya); Phaladeepika Ch. 13',
      clusterIII: 'Jaimini Upadesha Sutras 1.2.28 (Gnatikaraka Roga Sambandha)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Gochar Vedha'
    }
  },
  FAMILY: {
    category: 'FAMILY',
    primaryHouses: [2, 4, 9, 3],
    negationHouses: [12],
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Sun', 'Moon', 'Mars', 'Jupiter'],
    relevantVarga: 'D12',
    jaiminiKarakaRole: 'BK',
    citations: {
      clusterI: 'BPHS Ch. 13 & 14 (Dhana & Sahaja Bhava); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 15',
      clusterIII: 'Jaimini Upadesha Sutras 2.1 (Bhratrikaraka Pitrikaraka)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Gochar Phala'
    }
  },
  EDUCATION: {
    category: 'EDUCATION',
    primaryHouses: [4, 5, 9, 11],
    negationHouses: [3, 8],
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Mercury', 'Jupiter'],
    relevantVarga: 'D24',
    jaiminiKarakaRole: 'PK',
    citations: {
      clusterI: 'BPHS Ch. 15 & 16 (Vidya & Buddhi Bhava); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 11',
      clusterIII: 'Jaimini Upadesha Sutras 1.2 (Putrakaraka Vidya Sthana)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Phaladeepika Ch. 26'
    }
  },
  FINANCE: {
    category: 'FINANCE',
    primaryHouses: [2, 11, 5, 9],
    negationHouses: [12], // 12th = expenditure/loss
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Jupiter', 'Mercury', 'Venus'],
    relevantVarga: 'D1',
    jaiminiKarakaRole: 'AmK',
    citations: {
      clusterI: 'BPHS Ch. 13 & 41 (Dhana Bhava & Dhana Yogas); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Saravali Ch. 34',
      clusterIII: 'Jaimini Upadesha Sutras 1.2; BPHS Ch. 32',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Phaladeepika Ch. 26'
    }
  },
  LEGAL: {
    category: 'LEGAL',
    primaryHouses: [6, 9, 11],
    negationHouses: [12],
    dusthanaHouses: [8, 12],
    primaryKarakas: ['Jupiter', 'Mars', 'Saturn'],
    relevantVarga: 'D10',
    jaiminiKarakaRole: 'GK',
    citations: {
      clusterI: 'BPHS Ch. 17 & 21 (Satru & Dharma Bhava); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Prashna Marga Ch. 17',
      clusterIII: 'Jaimini Upadesha Sutras 1.2 (Gnatikaraka Vivada Phala)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Gochar Phala'
    }
  },
  SPIRITUAL: {
    category: 'SPIRITUAL',
    primaryHouses: [9, 12, 5, 8],
    negationHouses: [3],
    dusthanaHouses: [6],
    primaryKarakas: ['Jupiter', 'Ketu'],
    relevantVarga: 'D20',
    jaiminiKarakaRole: 'AK',
    citations: {
      clusterI: 'BPHS Ch. 21 & 45 (Dharma & Moksha Bhava); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 18',
      clusterIII: 'Jaimini Upadesha Sutras 1.1.28 & 2.1 (Atmakaraka Moksha Sthana)',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Gochar Phala'
    }
  },
  OTHER: {
    category: 'OTHER',
    primaryHouses: [1, 10, 11],
    negationHouses: [12],
    dusthanaHouses: [6, 8, 12],
    primaryKarakas: ['Sun', 'Jupiter'],
    relevantVarga: 'D1',
    jaiminiKarakaRole: 'AK',
    citations: {
      clusterI: 'BPHS Ch. 12 (Tanu Bhava & Samanya Phala); KP Reader Vol. IV',
      clusterII: 'BPHS Ch. 46; Phaladeepika Ch. 10',
      clusterIII: 'Jaimini Upadesha Sutras 1.1; BPHS Ch. 32',
      clusterIV: 'Devakeralam (Chandrakala Nadi); Gochar Phala'
    }
  }
};

// ============================================================================
// 2. CANDIDATE CHART CONTEXT & HELPER INTERFACES
// ============================================================================

export interface CandidateChartContext {
  birthDate: string; // YYYY-MM-DD
  birthTime: string; // HH:mm:ss
  latitude: number;
  longitude: number;
  placeName?: string;
  timezoneOffsetMinutes: number;
  ayanamsha?: string;
  nodeConvention?: 'mean' | 'true';
  precomputedKundali?: CompleteKundali;
}

// ============================================================================
// 3. CANONICAL JYOTISH CORE EXTRACTIONS
// ============================================================================

/**
 * Resolves or constructs a CompleteKundali for the candidate moment.
 */
export function resolveCandidateKundali(
  context: CandidateChartContext,
  plateau?: AstrologicalPlateauInterval
): CompleteKundali {
  if (context.precomputedKundali) {
    return context.precomputedKundali;
  }

  // Prioritize the plateau's actual startLocalTime and localDate over static context
  const birthTime = (plateau && plateau.startLocalTime) ? plateau.startLocalTime : (context.birthTime || '12:00:00');
  const birthDate = (plateau && plateau.localDate) ? plateau.localDate : context.birthDate;
  const tzOffset = (plateau && plateau.timezoneOffsetMinutes !== undefined) ? plateau.timezoneOffsetMinutes : context.timezoneOffsetMinutes;

  const details: BirthDetails = {
    name: 'BTR Candidate',
    gender: 'male',
    date: birthDate,
    time: birthTime,
    latitude: context.latitude,
    longitude: context.longitude,
    placeName: context.placeName || 'Rectification Coordinate',
    timezoneOffsetMinutes: tzOffset,
    ayanamsha: context.ayanamsha,
    nodeConvention: context.nodeConvention || 'mean'
  };

  return calculateKundali(details);
}

/**
 * Finds the active Mahadasha and Antardasha lords from the Kundali dasha hierarchy.
 */
export function findActiveDashaLords(
  kundali: CompleteKundali,
  eventDateMs: number
): { activeMdLord?: PlanetName; activeAdLord?: PlanetName } {
  if (!kundali.dashas || kundali.dashas.length === 0) {
    return {};
  }

  for (const md of kundali.dashas) {
    const mdStartMs = new Date(md.startDate).getTime();
    const mdEndMs = new Date(md.endDate).getTime();

    if (eventDateMs >= mdStartMs && eventDateMs < mdEndMs) {
      const activeMdLord = md.planet as PlanetName;
      let activeAdLord: PlanetName | undefined = undefined;

      if (md.antardashas && md.antardashas.length > 0) {
        for (const ad of md.antardashas) {
          const adStartMs = new Date(ad.startDate).getTime();
          const adEndMs = new Date(ad.endDate).getTime();

          if (eventDateMs >= adStartMs && eventDateMs < adEndMs) {
            activeAdLord = ad.planet as PlanetName;
            break;
          }
        }
      }

      return { activeMdLord, activeAdLord };
    }
  }

  return {};
}

/**
 * Maps a ZodiacSign to its canonical ruler PlanetName.
 */
export function getSignLord(sign: ZodiacSign): PlanetName {
  const lordStr = ZODIAC_LORDS[sign];
  return (lordStr || 'Mars') as PlanetName;
}

export interface BtrEvidenceDependencies {
  calculateDoubleTransit?: (
    natalPlanetsOrAscSignIndex: PlanetPosition[] | number,
    transitDate?: Date,
    context?: CalculationContext | NodeConvention
  ) => number[];
  calculateLivePlanetaryEphemeris?: typeof calculateLivePlanetaryEphemeris;
}

// ============================================================================
// 4. SINGLE EVENT EVALUATOR ACROSS 4 PHYSICAL VARIABLE CLUSTERS
// ============================================================================

/**
 * Evaluates a single life event against candidate chart & plateau across all
 * 4 physical variable clusters without subjective weights.
 */
export function evaluateEventAgainstPlateau(
  plateau: AstrologicalPlateauInterval,
  event: BtrLifeEventInput,
  kundali: CompleteKundali,
  birthDateMs: number,
  dependencies?: BtrEvidenceDependencies
): CategoricalEvidenceItem[] {
  const items: CategoricalEvidenceItem[] = [];
  const cfg = CANONICAL_SHASHTRIC_CONFIGS[event.category] || CANONICAL_SHASHTRIC_CONFIGS.OTHER;

  // Approximate dates are intervals, not point dates. Until interval-aware
  // event-time evaluation is implemented, do not silently substitute a
  // midpoint (YYYY -> June 15; YYYY-MM -> day 15) for a user's uncertain date.
  if (/^\d{4}$/.test(event.date) || /^\d{4}-(0[1-9]|1[0-2])$/.test(event.date)) {
    return [{
      ruleId: `R-${event.category}-DATE-PRECISION`,
      ruleName: 'Approximate Event Date Precision Guard',
      shastraSource: 'AOS BTR Evidence Precision Contract',
      variableCluster: 'CLUSTER_II_LUNAR',
      correlatedVariables: ['event_date_precision'],
      domain: event.category,
      status: 'INSUFFICIENT_DATA',
      eventTier: event.tier,
      eventId: event.id,
      rationale: `Event date "${event.date}" is approximate. A year/month midpoint must not be treated as the actual event date; interval-aware evaluation is required before temporal scoring.`,
      contradictionSeverity: 'NONE',
      isVerifiedCalculation: false
    }];
  }

  // --------------------------------------------------------------------------
  // Pre-Check: Strict Date Validation & Birth Epoch Precedence
  // --------------------------------------------------------------------------
  const resolvedEventDate = event.date;

  if (!isValidCalendarDate(resolvedEventDate)) {
    const invalidDateItem: CategoricalEvidenceItem = {
      ruleId: `R-${event.category}-DATE-VALID`,
      ruleName: 'Civil Calendar Date Contract Assertion',
      shastraSource: 'AOS Calendar Validation Contract',
      variableCluster: 'CLUSTER_I_ASCENDANT',
      correlatedVariables: ['event_date_format'],
      domain: event.category,
      status: 'INSUFFICIENT_DATA',
      eventTier: event.tier,
      eventId: event.id,
      rationale: `Invalid civil calendar date format: "${event.date}". Must be real Gregorian YYYY-MM-DD or standard YYYY / YYYY-MM.`,
      contradictionSeverity: 'NONE',
      isVerifiedCalculation: false
    };
    return [invalidDateItem];
  }

  const eventDateObj = new Date(resolvedEventDate + 'T12:00:00Z');
  const eventDateMs = eventDateObj.getTime();

  if (eventDateMs < birthDateMs) {
    const preBirthItem: CategoricalEvidenceItem = {
      ruleId: `R-${event.category}-PRE-BIRTH`,
      ruleName: 'Event Temporal Precedence Assertion',
      shastraSource: 'BPHS Ch. 46 (Temporal Order of Life Events)',
      variableCluster: 'CLUSTER_II_LUNAR',
      correlatedVariables: ['event_epoch_timestamp'],
      domain: event.category,
      status: 'INSUFFICIENT_DATA',
      eventTier: event.tier,
      eventId: event.id,
      rationale: `Event date ${event.date} is prior to candidate birth date. Life events cannot precede birth.`,
      contradictionSeverity: 'NONE',
      isVerifiedCalculation: false
    };
    return [preBirthItem];
  }

  // --------------------------------------------------------------------------
  // CLUSTER I: ASCENDANT & DIURNAL ROTATION (~24h Cycle)
  // Techniques: KP Cuspal Sub-Lords (H1, H7, H10), D1/D9/D10 Lagna Invariants
  // --------------------------------------------------------------------------
  const primaryHouse = cfg.primaryHouses[0] || 1;
  let kpSubLord: PlanetName | undefined = undefined;

  if (primaryHouse === 1) {
    kpSubLord = plateau.invariantsHeld.kpCuspSubLordHouse1;
  } else if (primaryHouse === 7) {
    kpSubLord = plateau.invariantsHeld.kpCuspSubLordHouse7;
  } else if (primaryHouse === 10) {
    kpSubLord = plateau.invariantsHeld.kpCuspSubLordHouse10;
  } else {
    // Look up via kundali house cusps
    const targetHouse = kundali.houses.find(h => h.houseNumber === primaryHouse);
    if (targetHouse) {
      const signIdx = ZODIAC_SIGNS.indexOf(targetHouse.sign);
      const houseDeg = targetHouse.degreeStart !== undefined ? targetHouse.degreeStart : signIdx * 30;
      kpSubLord = getKPLordsForLongitude(houseDeg).subLord;
    } else {
      kpSubLord = plateau.invariantsHeld.kpCuspSubLordHouse1;
    }
  }

  const subLordPlanet = kundali.planets.find(p => p.name === kpSubLord);
  let clusterIStatus: CategoricalEvidenceStatus = 'NEUTRAL';
  let clusterISeverity: 'CRITICAL' | 'MODERATE' | 'NONE' = 'NONE';
  let clusterIRationale = '';

  if (subLordPlanet) {
    const signifiesPrimary = cfg.primaryHouses.includes(subLordPlanet.house);
    const occupiesNegation = cfg.negationHouses.includes(subLordPlanet.house);

    if (signifiesPrimary) {
      clusterIStatus = 'SUPPORTS';
      clusterIRationale = `KP cusp sub-lord for House ${primaryHouse} (${kpSubLord}) signifies primary manifestation house ${subLordPlanet.house} without obstruction.`;
    } else if (occupiesNegation && !signifiesPrimary) {
      clusterIStatus = 'CONTRADICTS';
      clusterISeverity = event.tier === 'TIER_A' ? 'CRITICAL' : 'MODERATE';
      clusterIRationale = `KP cusp sub-lord for House ${primaryHouse} (${kpSubLord}) occupies negation house ${subLordPlanet.house} for ${event.category} with zero primary house links.`;
    } else {
      clusterIStatus = 'NEUTRAL';
      clusterIRationale = `KP cusp sub-lord for House ${primaryHouse} (${kpSubLord}) placed in House ${subLordPlanet.house}; neutral manifestation alignment.`;
    }
  } else {
    clusterIStatus = 'NEUTRAL';
    clusterIRationale = `KP cusp sub-lord for House ${primaryHouse} (${kpSubLord}) evaluated with neutral baseline.`;
  }

  items.push({
    ruleId: `R-${event.category}-KP-CUSP-SUB`,
    ruleName: `KP Cusp Sub-Lord Alignment (House ${primaryHouse})`,
    shastraSource: cfg.citations.clusterI,
    variableCluster: 'CLUSTER_I_ASCENDANT',
    correlatedVariables: ['kp_cusp_sub_lords', 'd1_ascendant_degree', 'diurnal_earth_rotation'],
    domain: event.category,
    status: clusterIStatus,
    eventTier: event.tier,
    eventId: event.id,
    rationale: clusterIRationale,
    contradictionSeverity: clusterISeverity,
    isVerifiedCalculation: true
  });

  // --------------------------------------------------------------------------
  // CLUSTER II: LUNAR MOTION & VIMSHOTTARI DASHA (~27.3d Moon Cycle)
  // Techniques: Active Mahadasha & Antardasha Lord Signification
  // --------------------------------------------------------------------------
  const { activeMdLord, activeAdLord } = findActiveDashaLords(kundali, eventDateMs);
  let clusterIIStatus: CategoricalEvidenceStatus = 'NEUTRAL';
  let clusterIISeverity: 'CRITICAL' | 'MODERATE' | 'NONE' = 'NONE';
  let clusterIIRationale = '';

  if (!activeMdLord || !activeAdLord) {
    clusterIIStatus = 'INSUFFICIENT_DATA';
    clusterIIRationale = `Event date ${event.date} falls outside computed Vimshottari dasha hierarchy.`;
  } else {
    const mdPlanet = kundali.planets.find(p => p.name === activeMdLord);
    const adPlanet = kundali.planets.find(p => p.name === activeAdLord);

    const mdSignifies = Boolean(
      (mdPlanet && cfg.primaryHouses.includes(mdPlanet.house)) ||
      cfg.primaryKarakas.includes(activeMdLord)
    );
    const adSignifies = Boolean(
      (adPlanet && cfg.primaryHouses.includes(adPlanet.house)) ||
      cfg.primaryKarakas.includes(activeAdLord)
    );

    const isMdDusthana = Boolean(
      mdPlanet && cfg.dusthanaHouses.includes(mdPlanet.house) && !cfg.primaryHouses.includes(mdPlanet.house)
    );
    const isAdDusthana = Boolean(
      adPlanet && cfg.dusthanaHouses.includes(adPlanet.house) && !cfg.primaryHouses.includes(adPlanet.house)
    );

    if (mdSignifies && adSignifies) {
      clusterIIStatus = 'SUPPORTS';
      clusterIIRationale = `Simultaneous dasha activation: Mahadasha lord ${activeMdLord} and Antardasha lord ${activeAdLord} both energize event houses/karakas.`;
    } else if (mdSignifies || adSignifies) {
      clusterIIStatus = 'SUPPORTS';
      clusterIIRationale = `Dasha lord (${mdSignifies ? activeMdLord : activeAdLord}) directly activates primary signification for ${event.category}.`;
    } else if (isMdDusthana && isAdDusthana && cfg.dusthanaHouses.length > 0) {
      clusterIIStatus = 'CONTRADICTS';
      clusterIISeverity = event.tier === 'TIER_A' ? 'CRITICAL' : 'MODERATE';
      clusterIIRationale = `Event occurred during mutually unsupportive Dusthana Dasha (${activeMdLord}-${activeAdLord}) with zero primary house connection.`;
    } else {
      clusterIIStatus = 'NEUTRAL';
      clusterIIRationale = `Active Dasha (${activeMdLord}-${activeAdLord}) is general; neither directly affirms nor acute contradiction.`;
    }
  }

  items.push({
    ruleId: `R-${event.category}-VIMSHOTTARI-DASHA`,
    ruleName: 'Vimshottari Mahadasha & Antardasha Timing',
    shastraSource: cfg.citations.clusterII,
    variableCluster: 'CLUSTER_II_LUNAR',
    correlatedVariables: ['moon_longitude', 'janma_nakshatra', 'vimshottari_dasha_balance'],
    domain: event.category,
    status: clusterIIStatus,
    eventTier: event.tier,
    eventId: event.id,
    rationale: clusterIIRationale,
    contradictionSeverity: clusterIISeverity,
    isVerifiedCalculation: true
  });

  // --------------------------------------------------------------------------
  // CLUSTER III: PLANETARY LONGITUDES & JAIMINI CHARA KARAKAS (Annual/Multi-Year)
  // Techniques: Jaimini 7 Chara Karakas (DK, PK, AmK, MK, AK, BK, GK)
  // --------------------------------------------------------------------------
  const charaKarakas = calculateCharaKarakas(kundali.planets);
  const targetKaraka = charaKarakas.find(k => k.code === cfg.jaiminiKarakaRole);
  let clusterIIIStatus: CategoricalEvidenceStatus = 'NEUTRAL';
  let clusterIIIRationale = '';

  if (targetKaraka) {
    const karakaPlanet = kundali.planets.find(p => p.name === targetKaraka.planet);
    if (karakaPlanet) {
      const isBeneficHouse = [1, 2, 4, 5, 7, 9, 10, 11].includes(karakaPlanet.house);
      const isDusthana = [6, 8, 12].includes(karakaPlanet.house);

      if (isBeneficHouse) {
        clusterIIIStatus = 'SUPPORTS';
        clusterIIIRationale = `Jaimini ${targetKaraka.code} (${targetKaraka.planet} - ${targetKaraka.name}) is favorably placed in House ${karakaPlanet.house} (${karakaPlanet.sign}).`;
      } else if (isDusthana && cfg.dusthanaHouses.length > 0) {
        clusterIIIStatus = 'CONTRADICTS';
        clusterIIIRationale = `Jaimini ${targetKaraka.code} (${targetKaraka.planet}) occupies Dusthana House ${karakaPlanet.house} for ${event.category}.`;
      } else {
        clusterIIIStatus = 'NEUTRAL';
        clusterIIIRationale = `Jaimini ${targetKaraka.code} (${targetKaraka.planet}) placed in House ${karakaPlanet.house}; neutral dignity.`;
      }
    } else {
      clusterIIIStatus = 'NEUTRAL';
      clusterIIIRationale = `Jaimini ${targetKaraka.code} (${targetKaraka.planet}) evaluated with neutral baseline.`;
    }
  } else {
    clusterIIIStatus = 'NEUTRAL';
    clusterIIIRationale = `Jaimini karaka role ${cfg.jaiminiKarakaRole} evaluated with neutral fallback.`;
  }

  items.push({
    ruleId: `R-${event.category}-JAIMINI-CHARA-KARAKA`,
    ruleName: `Jaimini Chara Karaka Placement (${cfg.jaiminiKarakaRole})`,
    shastraSource: cfg.citations.clusterIII,
    variableCluster: 'CLUSTER_III_PLANETARY',
    correlatedVariables: ['planetary_longitudes', 'chara_karaka_rankings', 'heliodynamic_degrees'],
    domain: event.category,
    status: clusterIIIStatus,
    eventTier: event.tier,
    eventId: event.id,
    rationale: clusterIIIRationale,
    contradictionSeverity: clusterIIIStatus === 'CONTRADICTS' ? 'MODERATE' : 'NONE',
    isVerifiedCalculation: true
  });

  // --------------------------------------------------------------------------
  // CLUSTER IV: TRANSITS & DEVAKERALAM DOUBLE TRANSIT (Event Epoch Transits)
  // Techniques: Guru-Shani Gochar on Natal Event Houses
  // --------------------------------------------------------------------------
  let clusterIVStatus: CategoricalEvidenceStatus = 'NEUTRAL';
  let clusterIVRationale = '';
  let clusterIVVerified = true;

  try {
    const doubleTransitCalculator = dependencies?.calculateDoubleTransit ?? calculateDoubleTransit;
    const doubleTransitHouses = doubleTransitCalculator(kundali.planets, eventDateObj);

    if (
      !Array.isArray(doubleTransitHouses) ||
      !doubleTransitHouses.every(h => typeof h === 'number' && Number.isInteger(h) && h >= 1 && h <= 12)
    ) {
      clusterIVStatus = 'INSUFFICIENT_DATA';
      clusterIVVerified = false;
      clusterIVRationale = 'Double transit calculation produced invalid transit house data.';
    } else if (doubleTransitHouses.length === 0) {
      // Ambiguous empty array: calculateDoubleTransit() returns [] both when ephemeris resolution
      // for Jupiter or Saturn fails (vedicEngine line 275) and when the geometric house intersection is empty.
      // Inspect authoritative calculateLivePlanetaryEphemeris to verify required transit planets.
      const ephemCalculator = dependencies?.calculateLivePlanetaryEphemeris ?? calculateLivePlanetaryEphemeris;
      const liveEphem = ephemCalculator(eventDateObj);
      const jupiter = Array.isArray(liveEphem) ? liveEphem.find(p => p.planet === 'Jupiter') : undefined;
      const saturn = Array.isArray(liveEphem) ? liveEphem.find(p => p.planet === 'Saturn') : undefined;

      const hasValidTransitPlanets =
        jupiter != null &&
        typeof jupiter.signIndex === 'number' &&
        Number.isInteger(jupiter.signIndex) &&
        jupiter.signIndex >= 0 &&
        jupiter.signIndex <= 11 &&
        saturn != null &&
        typeof saturn.signIndex === 'number' &&
        Number.isInteger(saturn.signIndex) &&
        saturn.signIndex >= 0 &&
        saturn.signIndex <= 11;

      if (!hasValidTransitPlanets) {
        clusterIVStatus = 'INSUFFICIENT_DATA';
        clusterIVVerified = false;
        clusterIVRationale = 'Double transit calculation could not resolve required transit planets (Jupiter/Saturn) for event date.';
      } else {
        clusterIVStatus = 'NEUTRAL';
        clusterIVVerified = true;
        clusterIVRationale = 'Double transit energized no houses on event date; background transit alignment neutral.';
      }
    } else {
      const hasDirectMatch = cfg.primaryHouses.some(h => doubleTransitHouses.includes(h));
      const hasLagnaMatch = doubleTransitHouses.includes(1);

      if (hasDirectMatch) {
        clusterIVStatus = 'SUPPORTS';
        const matching = cfg.primaryHouses.filter(h => doubleTransitHouses.includes(h));
        clusterIVRationale = `Devakeralam Guru-Shani Double Transit energized natal primary event house(s) [${matching.join(', ')}] on event date.`;
      } else if (hasLagnaMatch) {
        clusterIVStatus = 'SUPPORTS';
        clusterIVRationale = 'Guru-Shani Double Transit energized natal Lagna (House 1), confirming physical milestone event window.';
      } else {
        clusterIVStatus = 'NEUTRAL';
        clusterIVRationale = `Double Transit energized houses [${doubleTransitHouses.join(', ')}]; background transit alignment neutral.`;
      }
    }
  } catch (err) {
    clusterIVStatus = 'INSUFFICIENT_DATA';
    clusterIVVerified = false;
    clusterIVRationale = 'Double transit calculation could not be evaluated for event date.';
  }

  items.push({
    ruleId: `R-${event.category}-DOUBLE-TRANSIT`,
    ruleName: 'Devakeralam Guru-Shani Double Transit Alignment',
    shastraSource: cfg.citations.clusterIV,
    variableCluster: 'CLUSTER_IV_TRANSIT',
    correlatedVariables: ['jupiter_transit_longitude', 'saturn_transit_longitude', 'event_epoch_transits'],
    domain: event.category,
    status: clusterIVStatus,
    eventTier: event.tier,
    eventId: event.id,
    rationale: clusterIVRationale,
    contradictionSeverity: 'NONE',
    isVerifiedCalculation: clusterIVVerified
  });

  return items;
}

// ============================================================================
// 5. MULTI-EVENT AGGREGATOR & PLATEAU EVIDENCE EVALUATION
// ============================================================================

/**
 * Evaluates an Astrological Plateau Interval against all supplied life events.
 * Aggregates evidence across the four physical variable clusters and enforces
 * critical contradiction disqualification on Tier A events.
 */
export function evaluatePlateauAgainstLifeEvents(
  plateau: AstrologicalPlateauInterval,
  events: BtrLifeEventInput[],
  context: CandidateChartContext,
  dependencies?: BtrEvidenceDependencies
): PlateauEvidenceEvaluation {
  const kundali = resolveCandidateKundali(context, plateau);
  const effectiveBirthDate = plateau.localDate || context.birthDate;
  const birthDateObj = new Date(effectiveBirthDate + 'T00:00:00Z');
  const birthDateMs = birthDateObj.getTime();

  const allItems: CategoricalEvidenceItem[] = [];

  for (const event of events) {
    const eventItems = evaluateEventAgainstPlateau(plateau, event, kundali, birthDateMs, dependencies);
    allItems.push(...eventItems);
  }

  // Initial cluster summary containers
  const clusterSummaries: Record<VariableClusterId, ClusterEvidenceSummary> = {
    CLUSTER_I_ASCENDANT: {
      clusterId: 'CLUSTER_I_ASCENDANT',
      supportingCount: 0,
      neutralCount: 0,
      contradictingCount: 0,
      insufficientCount: 0,
      clusterStatus: 'INSUFFICIENT_DATA',
      primaryTechniques: ['KP Cusp Sub-Lords', 'D1/D9/D10 Lagna Invariants']
    },
    CLUSTER_II_LUNAR: {
      clusterId: 'CLUSTER_II_LUNAR',
      supportingCount: 0,
      neutralCount: 0,
      contradictingCount: 0,
      insufficientCount: 0,
      clusterStatus: 'INSUFFICIENT_DATA',
      primaryTechniques: ['Vimshottari Mahadasha', 'Antardasha Sub-Periods']
    },
    CLUSTER_III_PLANETARY: {
      clusterId: 'CLUSTER_III_PLANETARY',
      supportingCount: 0,
      neutralCount: 0,
      contradictingCount: 0,
      insufficientCount: 0,
      clusterStatus: 'INSUFFICIENT_DATA',
      primaryTechniques: ['Jaimini Chara Karakas', 'Planetary Longitudes']
    },
    CLUSTER_IV_TRANSIT: {
      clusterId: 'CLUSTER_IV_TRANSIT',
      supportingCount: 0,
      neutralCount: 0,
      contradictingCount: 0,
      insufficientCount: 0,
      clusterStatus: 'INSUFFICIENT_DATA',
      primaryTechniques: ['Devakeralam Guru-Shani Double Transit']
    }
  };

  let criticalContradictionsCount = 0;
  let moderateContradictionsCount = 0;
  let isDisqualified = false;
  let disqualificationReason: string | undefined = undefined;

  for (const item of allItems) {
    const summary = clusterSummaries[item.variableCluster];

    if (item.status === 'SUPPORTS') {
      summary.supportingCount++;
    } else if (item.status === 'NEUTRAL') {
      summary.neutralCount++;
    } else if (item.status === 'CONTRADICTS') {
      summary.contradictingCount++;
      if (item.contradictionSeverity === 'CRITICAL') {
        criticalContradictionsCount++;
        // Critical Contradiction Disqualification Rule (Spec §8.4)
        if (item.eventTier === 'TIER_A') {
          isDisqualified = true;
          if (!disqualificationReason) {
            disqualificationReason = `Plateau disqualified by critical contradiction in Tier A event "${item.eventId}" (${item.domain}): ${item.rationale}`;
          }
        }
      } else if (item.contradictionSeverity === 'MODERATE') {
        moderateContradictionsCount++;
      }
    } else if (item.status === 'INSUFFICIENT_DATA') {
      summary.insufficientCount++;
    }
  }

  // Derive consolidated cluster statuses
  const clusterKeys: VariableClusterId[] = [
    'CLUSTER_I_ASCENDANT',
    'CLUSTER_II_LUNAR',
    'CLUSTER_III_PLANETARY',
    'CLUSTER_IV_TRANSIT'
  ];

  let independentClustersSupportedCount = 0;

  for (const cKey of clusterKeys) {
    const s = clusterSummaries[cKey];
    const totalItems = s.supportingCount + s.neutralCount + s.contradictingCount + s.insufficientCount;

    if (totalItems === 0) {
      s.clusterStatus = 'INSUFFICIENT_DATA';
    } else if (s.contradictingCount > 0 && s.contradictingCount >= s.supportingCount) {
      s.clusterStatus = 'CONTRADICTS';
    } else if (s.supportingCount > 0) {
      s.clusterStatus = 'SUPPORTS';
      independentClustersSupportedCount++;
    } else if (s.neutralCount > 0) {
      s.clusterStatus = 'NEUTRAL';
    } else {
      s.clusterStatus = 'INSUFFICIENT_DATA';
    }
  }

  // Derive overall convergence verdict
  let convergenceVerdict: 'CONVERGENT' | 'PARTIAL' | 'DISQUALIFIED' | 'INCONCLUSIVE';

  if (isDisqualified) {
    convergenceVerdict = 'DISQUALIFIED';
  } else if (events.length === 0) {
    convergenceVerdict = 'INCONCLUSIVE';
  } else if (independentClustersSupportedCount >= 2 && criticalContradictionsCount === 0) {
    convergenceVerdict = 'CONVERGENT';
  } else if (independentClustersSupportedCount === 1 && criticalContradictionsCount === 0) {
    convergenceVerdict = 'PARTIAL';
  } else {
    convergenceVerdict = 'INCONCLUSIVE';
  }

  return {
    plateauInterval: plateau,
    evaluatedEventsCount: events.length,
    evidenceItems: allItems,
    clusterSummaries,
    independentClustersSupportedCount,
    isDisqualified,
    disqualificationReason,
    criticalContradictionsCount,
    moderateContradictionsCount,
    convergenceVerdict
  };
}

